import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, Clock, Truck, CheckCircle } from 'lucide-react';

const InteractiveMap = () => {
  const { language } = useLanguage();
  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  const serviceAreas = [
    {
      id: 'woodlands',
      name: 'The Woodlands',
      coordinates: { x: 45, y: 35 },
      deliveryTime: language === 'en' ? 'Delivery Available' : 'Entrega Disponible',
      minOrder: '$150',
      population: '118,000+',
      specialties: language === 'en' 
        ? ['Residential Communities', 'HOA Projects', 'Commercial Developments']
        : ['Comunidades Residenciales', 'Proyectos HOA', 'Desarrollos Comerciales']
    },
    {
      id: 'magnolia',
      name: 'Magnolia',
      coordinates: { x: 55, y: 45 },
      deliveryTime: language === 'en' ? 'Delivery Available' : 'Entrega Disponible',
      minOrder: '$100',
      population: '2,000+',
      specialties: language === 'en'
        ? ['Rural Properties', 'Large Acreage', 'Custom Projects']
        : ['Propiedades Rurales', 'Grandes Extensiones', 'Proyectos Personalizados']
    },
    {
      id: 'conroe',
      name: 'Conroe',
      coordinates: { x: 60, y: 25 },
      deliveryTime: language === 'en' ? 'Next Day' : 'Día Siguiente',
      minOrder: '$125',
      population: '95,000+',
      specialties: language === 'en'
        ? ['City Projects', 'Commercial Landscaping', 'Municipal Contracts']
        : ['Proyectos de Ciudad', 'Paisajismo Comercial', 'Contratos Municipales']
    },
    {
      id: 'cypress',
      name: 'Cypress',
      coordinates: { x: 35, y: 60 },
      deliveryTime: language === 'en' ? 'Delivery Available' : 'Entrega Disponible',
      minOrder: '$150',
      population: '150,000+',
      specialties: language === 'en'
        ? ['New Construction', 'Subdivision Projects', 'Builder Partnerships']
        : ['Nueva Construcción', 'Proyectos de Subdivisión', 'Alianzas con Constructores']
    },
    {
      id: 'spring',
      name: 'Spring',
      coordinates: { x: 40, y: 55 },
      deliveryTime: language === 'en' ? 'Delivery Available' : 'Entrega Disponible',
      minOrder: '$135',
      population: '60,000+',
      specialties: language === 'en'
        ? ['Established Neighborhoods', 'Renovation Projects', 'Pool Landscaping']
        : ['Vecindarios Establecidos', 'Proyectos de Renovación', 'Paisajismo de Piscinas']
    }
  ];

  const selectedAreaData = serviceAreas.find(area => area.id === selectedArea);

  return (
    <div className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {language === 'en' ? 'Interactive Service Area Map' : 'Mapa Interactivo de Áreas de Servicio'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {language === 'en'
              ? 'Click on any area to see detailed delivery information and service specialties'
              : 'Haz clic en cualquier área para ver información detallada de entrega y especialidades de servicio'
            }
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Interactive Map */}
          <div className="relative">
            <div className="aspect-square bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl border-2 border-border relative overflow-hidden">
              {/* Map background */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50 opacity-50"></div>
              
              {/* Montgomery County outline */}
              <div className="absolute inset-4 border-2 border-dashed border-primary/30 rounded-xl flex items-center justify-center">
                <span className="text-primary/60 font-medium text-sm">
                  {language === 'en' ? 'Montgomery County, TX' : 'Condado Montgomery, TX'}
                </span>
              </div>

              {/* Service Area Points */}
              {serviceAreas.map((area) => (
                <button
                  key={area.id}
                  onClick={() => setSelectedArea(area.id)}
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ${
                    selectedArea === area.id 
                      ? 'scale-125 z-20' 
                      : 'hover:scale-110 z-10'
                  }`}
                  style={{
                    left: `${area.coordinates.x}%`,
                    top: `${area.coordinates.y}%`
                  }}
                >
                  <div className={`relative ${
                    selectedArea === area.id 
                      ? 'animate-pulse-glow' 
                      : ''
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                      selectedArea === area.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                    }`}>
                      <MapPin className="h-4 w-4" />
                    </div>
                    
                    {/* Area label */}
                    <div className={`absolute top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-opacity ${
                      selectedArea === area.id ? 'opacity-100' : 'opacity-0'
                    }`}>
                      <div className="bg-background border border-border rounded-lg px-3 py-1 shadow-lg">
                        <span className="text-sm font-medium text-foreground">{area.name}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}

              {/* Ripple effect for selected area */}
              {selectedAreaData && (
                <div
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                  style={{
                    left: `${selectedAreaData.coordinates.x}%`,
                    top: `${selectedAreaData.coordinates.y}%`
                  }}
                >
                  <div className="w-16 h-16 rounded-full border-2 border-primary/30 animate-ping"></div>
                </div>
              )}
            </div>
          </div>

          {/* Area Information */}
          <div className="space-y-6">
            {selectedAreaData ? (
              <Card className="animate-fade-in border-l-4 border-l-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <MapPin className="h-6 w-6 text-primary" />
                    {selectedAreaData.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Delivery Info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
                      <Truck className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Delivery Time' : 'Tiempo de Entrega'}
                        </p>
                        <p className="font-semibold text-foreground">{selectedAreaData.deliveryTime}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
                      <Clock className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Min. Order' : 'Orden Mín.'}
                        </p>
                        <p className="font-semibold text-foreground">{selectedAreaData.minOrder}</p>
                      </div>
                    </div>
                  </div>

                  {/* Population */}
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-primary border-primary">
                      {language === 'en' ? 'Population:' : 'Población:'} {selectedAreaData.population}
                    </Badge>
                  </div>

                  {/* Specialties */}
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      {language === 'en' ? 'Service Specialties' : 'Especialidades de Servicio'}
                    </h4>
                    <div className="space-y-2">
                      {selectedAreaData.specialties.map((specialty, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          <span className="text-muted-foreground">{specialty}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact Button */}
                  <Button variant="hero" className="w-full">
                    <Phone className="mr-2 h-4 w-4" />
                    {language === 'en' ? 'Get Quote for This Area' : 'Obtener Cotización para Esta Área'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed border-2 border-border">
                <CardContent className="p-12 text-center">
                  <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === 'en' ? 'Select a Service Area' : 'Selecciona un Área de Servicio'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Click on any location on the map to see detailed delivery information and service specialties for that area.'
                      : 'Haz clic en cualquier ubicación en el mapa para ver información detallada de entrega y especialidades de servicio para esa área.'
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;